import { useEffect, useRef } from 'react';
import {
  getGpsLostHexagonStyle,
  getPortStyle,
  getSourceIdentifierStyle,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { useAISMapContext } from './AISMapContext';
import { Feature } from 'ol';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { useCoverage } from './Coverage/CoverageContext';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { GpsLostGridCell, useGPSLost } from './GpsLost/GPSLostContext';
import type OlGeometry from 'ol/geom/Geometry';

/**
 * Hook to synchronize selected ports with their styles on the map.
 */
export const useSelectedPortStyling = () => {
  const { map } = useOpenLayersContext();
  const { selectedPorts } = useSelectionState();

  useEffect(() => {
    if (!map) return;

    // Find the specific layer with ID 'gatehouse-ports'
    const layer = map
      .getLayers()
      .getArray()
      .find((layer) => layer.get('id') === 'gatehouse-ports') as VectorLayer<
      Feature<OlGeometry>
    >;
    if (!layer) return; // Exit if the layer is not found

    const source = layer.getSource();
    if (!source) return;

    const selectStyle = getPortStyle('large');

    // Iterate over the features in the specific layer
    source.getFeatures().forEach((feature: Feature) => {
      const featureId = feature.get('id');

      const isSelected = selectedPorts.some((port) => port.id === featureId);

      if (isSelected) {
        feature.setStyle(selectStyle);
      } else {
        feature.setStyle();
      }
    });
  }, [map, selectedPorts]);
};

export const useSelectedSourceIdentifiersStyling = () => {
  const { map } = useOpenLayersContext();
  const { selectedSourceIdentifiers } = useCoverage();

  useEffect(() => {
    if (!map) return;

    const layer = map
      .getLayers()
      .getArray()
      .find((layer) => layer.get('id') === 'source-identifiers') as VectorLayer<
      Feature<OlGeometry>
    >;
    if (!layer) return; // Exit if the layer is not found

    const source = layer.getSource();
    if (!source) return;

    const selectStyle = getSourceIdentifierStyle('large');

    // Iterate over the features in the specific layer
    source.getFeatures().forEach((feature: Feature) => {
      const featureId = feature.get('id');
      const isSelected = selectedSourceIdentifiers.some(
        (sourceIdentifier) => sourceIdentifier.id === parseInt(featureId)
      );

      if (isSelected) {
        feature.setStyle(selectStyle);
      } else {
        feature.setStyle();
      }
    });
  }, [map, selectedSourceIdentifiers]);
};

export const useSelectedGpsLostGridCellStyling = () => {
  const { map } = useOpenLayersContext();
  const { selectedGPSLostGridCell, heatmapConfig } = useGPSLost();

  const prevSelectedGPSLostGridCell = useRef<GpsLostGridCell | null>(null);

  useEffect(() => {
    if (!map) return;

    if (!selectedGPSLostGridCell) {
      // If no cell is selected, reset the style of the previously selected cell
      const layer = map
        .getLayers()
        .getArray()
        .find(
          (layer) =>
            layer.get('id') === prevSelectedGPSLostGridCell.current?.gridLayerId
        ) as VectorLayer<Feature>;
      if (!layer) return;

      const source = layer.getSource();
      if (!source) return;

      source.getFeatures().forEach((feature: Feature) => {
        feature.setStyle();
      });
      return;
    }

    const layer = map
      .getLayers()
      .getArray()
      .find(
        (layer) => layer.get('id') === selectedGPSLostGridCell.gridLayerId
      ) as VectorLayer<Feature<OlGeometry>>;
    if (!layer) return;

    const source = layer.getSource();
    if (!source) return;

    // Iterate over the features in the specific layer
    source.getFeatures().forEach((feature: Feature) => {
      const featureId = feature.get('id');
      const isSelected = selectedGPSLostGridCell.id === featureId;

      if (isSelected) {
        const selectStyle = getGpsLostHexagonStyle(
          feature,
          heatmapConfig,
          true
        );
        feature.setStyle(selectStyle);
      } else {
        feature.setStyle();
      }
    });

    prevSelectedGPSLostGridCell.current = selectedGPSLostGridCell;
  }, [map, selectedGPSLostGridCell]);
};
